import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { outlineOnDarkClass } from '@/lib/ctaStyles';
import { eyebrowClass, cardClass } from '@/lib/sectionStyles';

const WIZARD_STEPS = [
  {
    n: '01',
    title: 'Define',
    description:
      'Establish agent identity, purpose, and operational boundaries. Set the business goal and desired results — or refine a template from the pre-built library.',
  },
  {
    n: '02',
    title: 'Build',
    description:
      'Configure capabilities, prompts, tools, and integrations. The AI authoring assistant reads, proposes, and validates as you go.',
  },
  {
    n: '03',
    title: 'Govern',
    description:
      'Set policies, approval gates, and compliance controls — risk tier, operating mode, and human-in-the-loop points are declared here, before deployment.',
  },
  {
    n: '04',
    title: 'Validate',
    description:
      'Test, assess risks, and finalize for deployment. Shadow mode runs proposed changes against production behavior; a propose → approve → promote pipeline gates every change.',
  },
];

const CAPABILITIES = [
  {
    title: 'Workflow Designer',
    description:
      'Compose deterministic, auditable workflows on a visual, no-code canvas — triggers, tasks, decisions, and actions with human handoffs where they belong. Workflows validate at save time and run on a deterministic engine, so what you designed is what executes.',
  },
  {
    title: 'Skills & best practices',
    description:
      'Package expertise as reusable skills and inject organizational knowledge into agent decisions. Skills compose across agents, so what one team learns, every agent can use.',
  },
  {
    title: 'Any agent. Your rules.',
    description:
      "Workflow steps can be executed by CriticalBridge agents, point-solution agents, and your SaaS platforms' agents alike — but only the agents you've enrolled, licensed, and cleared for that step are ever eligible, and the runtime refuses anything quarantined, retired, or outside its approved scope.",
  },
  {
    title: 'Connectors',
    description:
      'Typed adapters for the systems agents actually touch: Guidewire ClaimCenter, databases, email, Slack, ticketing, HTTP/webhook, WebSocket, and MCP servers — with credentials vault-resolved and scoped per task.',
  },
];

export function StudioPage() {
  return (
    <>
      <Helmet>
        <title>Agent Studio - CriticalBridge.AI</title>
        <meta
          name="description"
          content="Agent Studio is where insurance workflows get built: a visual designer for end-to-end processes, a guided agent wizard with an AI authoring assistant, a skills builder, and a rules engine — with policy checks, approvals, and audit wired into every step at design time."
        />
        <meta
          name="keywords"
          content="agent studio, no-code workflow builder, ai agent wizard, insurance workflow automation"
        />
        <meta property="og:title" content="Agent Studio - CriticalBridge.AI" />
        <meta
          property="og:description"
          content="Build agents and workflows with governance built in — not bolted on."
        />
        <link rel="canonical" href="https://www.criticalbridge.ai/studio" />
      </Helmet>

      {/* Hero */}
      <section className="bg-secondary">
        <div className="container mx-auto px-4 py-16 sm:py-20">
          <div className="font-mono text-xs text-secondary-foreground/60">
            <Link to="/" className="hover:text-secondary-foreground">
              Home
            </Link>{' '}
            / Products / Agent Studio
          </div>
          <span className="mt-4 block font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            Build
          </span>
          <h1 className="mt-2 max-w-[22ch] text-4xl font-extrabold leading-tight text-secondary-foreground sm:text-5xl">
            Build agents and workflows with governance built in — not bolted on.
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-secondary-foreground/70">
            Agent Studio is where insurance workflows get built: a visual designer for end-to-end
            processes, a guided agent wizard with an AI authoring assistant, a skills builder, and a
            rules engine — with policy checks, approvals, and audit wired into every step at design
            time.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button size="lg" asChild>
              <Link to="/contact">Book a working session</Link>
            </Button>
            <Button size="lg" variant="outline" className={outlineOnDarkClass} asChild>
              <Link to="/platform">How the platform fits together</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* The wizard */}
      <section className="border-y bg-card py-16">
        <div className="container mx-auto px-4">
          <span className={eyebrowClass}>The wizard</span>
          <h2 className="mt-2 text-3xl font-bold">Govern is a build phase, not an afterthought.</h2>
          <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
            The agent wizard walks every new agent through four phases — so nothing reaches
            production without boundaries, policies, and validation.
          </p>

          <div className="mt-8 divide-y divide-border">
            {WIZARD_STEPS.map((step) => (
              <div
                key={step.n}
                className="grid grid-cols-[56px_1fr] gap-4 py-6 first:pt-0 sm:grid-cols-[64px_1fr]"
              >
                <div className="font-display text-2xl font-extrabold text-primary">{step.n}</div>
                <div>
                  <h3 className="text-lg font-bold">{step.title}</h3>
                  <p className="mt-1.5 max-w-[64ch] text-[15.5px] leading-relaxed text-muted-foreground">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <span className={eyebrowClass}>Capabilities</span>
          <h2 className="mt-2 text-3xl font-bold">
            Everything a builder needs, in one environment.
          </h2>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {CAPABILITIES.map((capability) => (
              <div key={capability.title} className={cardClass}>
                <h3 className="text-lg font-bold">{capability.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{capability.description}</p>
              </div>
            ))}
          </div>

          <Button variant="outline" className="mt-6" asChild>
            <Link to="/console">See where the work happens →</Link>
          </Button>
        </div>
      </section>
    </>
  );
}
